// Story 11.4 — Buffer + filtros del LogPanel. Aislado de React para tests.

import type { LogEntry, LogLevel } from './event-to-log';

export const LOG_BUFFER_MAX = 5000;

export const ALL_LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error'];

export interface LogFilters {
  /** Niveles activos. Default: todos. */
  levels: LogLevel[];
  /** Substring a buscar en mensaje + context flatten. */
  search: string;
}

export const initialFilters: LogFilters = {
  levels: [...ALL_LEVELS],
  search: '',
};

/**
 * Append-or-drop: respeta `LOG_BUFFER_MAX` haciendo FIFO eviction. Crea un
 * nuevo array (no mutate) para que React detecte el cambio.
 */
export function appendToBuffer(buffer: LogEntry[], entry: LogEntry): LogEntry[] {
  if (buffer.length >= LOG_BUFFER_MAX) {
    return [...buffer.slice(buffer.length - LOG_BUFFER_MAX + 1), entry];
  }
  return [...buffer, entry];
}

/** Cuenta errors / warnings en el buffer. */
export function countSeverities(buffer: LogEntry[]): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const e of buffer) {
    if (e.level === 'error') errors++;
    else if (e.level === 'warn') warnings++;
  }
  return { errors, warnings };
}

/**
 * Aplica filtros sobre el buffer. Pure y memoizable; el caller debe
 * `useMemo(() => filterLogs(buffer, filters), [buffer, filters])`.
 */
export function filterLogs(buffer: LogEntry[], filters: LogFilters): LogEntry[] {
  const activeLevels = new Set(filters.levels);
  const needle = filters.search.trim().toLowerCase();
  const out: LogEntry[] = [];
  for (const entry of buffer) {
    if (!activeLevels.has(entry.level)) continue;
    if (needle.length > 0 && !entryMatches(entry, needle)) continue;
    out.push(entry);
  }
  return out;
}

function entryMatches(entry: LogEntry, needle: string): boolean {
  if (entry.message.toLowerCase().includes(needle)) return true;
  if (entry.eventType.toLowerCase().includes(needle)) return true;
  // Flatten del context: convierte a string y busca substring.
  try {
    const flat = JSON.stringify(entry.context).toLowerCase();
    if (flat.includes(needle)) return true;
  } catch {
    /* cyclic — skip */
  }
  return false;
}

/** Parsea search params del URL: `?logLevel=warn,error&logSearch=foo`. */
export function parseLogFiltersFromSearch(search: Record<string, unknown>): LogFilters {
  const levelRaw = typeof search.logLevel === 'string' ? search.logLevel : '';
  const levels = levelRaw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is LogLevel => ALL_LEVELS.includes(s as LogLevel));
  const searchTerm = typeof search.logSearch === 'string' ? search.logSearch : '';
  return {
    levels: levels.length > 0 ? levels : [...ALL_LEVELS],
    search: searchTerm,
  };
}

/** Convierte filtros al shape de URL search params (no escribe los defaults). */
export function logFiltersToSearch(filters: LogFilters): {
  logLevel?: string;
  logSearch?: string;
} {
  const out: { logLevel?: string; logSearch?: string } = {};
  if (filters.levels.length < ALL_LEVELS.length) {
    out.logLevel = filters.levels.join(',');
  }
  if (filters.search.length > 0) out.logSearch = filters.search;
  return out;
}

/**
 * `isAtBottom` heurística para auto-scroll. 50px de tolerancia.
 */
export function isScrolledToBottom(
  scrollHeight: number,
  scrollTop: number,
  clientHeight: number,
): boolean {
  return scrollHeight - scrollTop - clientHeight < 50;
}
